import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const BeatPlansApi = createApi({
  reducerPath: "BeatPlans",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_BACKEND2_URL}/v1/beat-plans`,
    credentials: "include",
  }),
  endpoints: (builder) => ({
    // List + filters
    getAllBeatPlans: builder.query({
      query: ({
        page = 1,
        limit,
        sortBy,
        sortOrder,
        search,
        status,
        assignedToId,
      } = {}) => {
        const queryParams = new URLSearchParams();
        if (page) queryParams.append("page", page);
        if (limit) queryParams.append("limit", limit);
        if (sortBy) queryParams.append("sortBy", sortBy);
        if (sortOrder) queryParams.append("sortOrder", sortOrder);
        if (search) queryParams.append("search", search);
        if (status) queryParams.append("status", status);
        if (assignedToId) queryParams.append("assignedToId", assignedToId);
        return `?${queryParams.toString()}`;
      },
    }),

    // Single beat plan (with stops)
    getSingleBeatPlan: builder.query({
      query: (id) => `/${id}`,
    }),

    // Create
    createBeatPlan: builder.mutation({
      query: (payload) => ({
        url: "",
        method: "POST",
        body: payload,
      }),
    }),

    // Update plan info
    editBeatPlan: builder.mutation({
      query: ({ id, payload }) => ({
        url: `/${id}`,
        method: "PATCH",
        body: payload,
      }),
    }),

    // Delete (archive)
    deleteBeatPlan: builder.mutation({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
    }),

    // Pause / resume
    toggleBeatPlanStatus: builder.mutation({
      query: ({ id, action }) => ({
        // action: "pause" | "resume"
        url: `/${id}/${action}`,
        method: "POST",
      }),
    }),

    // Clone (optionally swap rep)
    cloneBeatPlan: builder.mutation({
      query: ({ id, payload }) => ({
        url: `/${id}/clone`,
        method: "POST",
        body: payload,
      }),
    }),

    // Stops management
    addStops: builder.mutation({
      query: ({ id, payload }) => ({
        url: `/${id}/stops`,
        method: "POST",
        body: payload,
      }),
    }),
    updateStop: builder.mutation({
      query: ({ id, stopId, payload }) => ({
        url: `/${id}/stops/${stopId}`,
        method: "PATCH",
        body: payload,
      }),
    }),
    removeStop: builder.mutation({
      query: ({ id, stopId }) => ({
        url: `/${id}/stops/${stopId}`,
        method: "DELETE",
      }),
    }),
    reorderStops: builder.mutation({
      query: ({ id, payload }) => ({
        url: `/${id}/stops/reorder`,
        method: "PUT",
        body: payload,
      }),
    }),

    // Route optimization
    optimizeRoute: builder.mutation({
      query: ({ id }) => ({
        url: `/${id}/optimize-route`,
        method: "POST",
      }),
    }),

    // Manual generation (for testing / on-demand)
    generateNow: builder.mutation({
      query: ({ id, payload }) => ({
        url: `/${id}/generate-now`,
        method: "POST",
        body: payload,
      }),
    }),

    // Audit / history of generated tasks
    getBeatPlanExecutions: builder.query({
      query: ({ id, page = 1, limit = 50 } = {}) => {
        const queryParams = new URLSearchParams();
        queryParams.append("page", page);
        queryParams.append("limit", limit);
        return `/${id}/executions?${queryParams.toString()}`;
      },
    }),

    // Dry-run preview (next N days)
    getBeatPlanPreview: builder.query({
      query: ({ id, days = 7 } = {}) => `/${id}/preview?days=${days}`,
    }),
  }),
});

export const {
  useGetAllBeatPlansQuery,
  useGetSingleBeatPlanQuery,
  useCreateBeatPlanMutation,
  useEditBeatPlanMutation,
  useDeleteBeatPlanMutation,
  useToggleBeatPlanStatusMutation,
  useCloneBeatPlanMutation,
  useAddStopsMutation,
  useUpdateStopMutation,
  useRemoveStopMutation,
  useReorderStopsMutation,
  useOptimizeRouteMutation,
  useGenerateNowMutation,
  useGetBeatPlanExecutionsQuery,
  useGetBeatPlanPreviewQuery,
} = BeatPlansApi;